import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supplierAuthApi } from '../api/supplierAuthApi';
import { ApiError, isSupplierAuthPath } from '../api/client';

const SupplierAuthContext = createContext();

export const useSupplierAuth = () => {
  const context = useContext(SupplierAuthContext);
  if (!context) {
    throw new Error('useSupplierAuth must be used within SupplierAuthProvider');
  }
  return context;
};

export const SupplierAuthProvider = ({ children }) => {
  const [supplierCode, setSupplierCode] = useState(() => {
    return localStorage.getItem('supplier_code') || null;
  });
  const [sessionToken, setSessionToken] = useState(() => {
    return localStorage.getItem('supplier_session_token') || null;
  });
  const [supplierName, setSupplierName] = useState(() => {
    return localStorage.getItem('supplier_name') || null;
  });
  const [sessionChecked, setSessionChecked] = useState(false);

  useEffect(() => {
    if (supplierCode && sessionToken) {
      localStorage.setItem('supplier_code', supplierCode);
      localStorage.setItem('supplier_session_token', sessionToken);
    } else {
      localStorage.removeItem('supplier_code');
      localStorage.removeItem('supplier_session_token');
      localStorage.removeItem('supplier_name');
    }
  }, [supplierCode, sessionToken]);

  useEffect(() => {
    if (supplierName) {
      localStorage.setItem('supplier_name', supplierName);
    }
  }, [supplierName]);

  const logout = useCallback(() => {
    setSupplierCode(null);
    setSessionToken(null);
    setSupplierName(null);
    localStorage.removeItem('supplier_code');
    localStorage.removeItem('supplier_session_token');
    localStorage.removeItem('supplier_name');
  }, []);

  useEffect(() => {
    let cancelled = false;

    const validateSession = async () => {
      if (!sessionToken) {
        if (!cancelled) setSessionChecked(true);
        return;
      }
      // 社内タブでは仕入先 me を叩かない（トークン未送信 401 で編集セッションを消さない）
      if (!isSupplierAuthPath()) {
        if (!cancelled) setSessionChecked(true);
        return;
      }

      try {
        const me = await supplierAuthApi.getMe();
        if (cancelled) return;
        setSupplierCode(String(me.supplier_code));
        setSupplierName(me.supplier_name || null);
        setSessionChecked(true);
      } catch (error) {
        if (cancelled) return;
        if (error instanceof ApiError && error.status === 401 && isSupplierAuthPath()) {
          logout();
        }
        setSessionChecked(true);
      }
    };

    validateSession();

    return () => {
      cancelled = true;
    };
  }, [sessionToken, logout]);

  const login = (code, token, name) => {
    setSessionChecked(false);
    setSupplierCode(String(code));
    setSessionToken(token);
    setSupplierName(name);
  };

  const isAuthenticated = !!supplierCode && !!sessionToken && sessionChecked;

  return (
    <SupplierAuthContext.Provider
      value={{
        supplierCode,
        sessionToken,
        supplierName,
        isAuthenticated,
        sessionChecked,
        login,
        logout,
      }}
    >
      {children}
    </SupplierAuthContext.Provider>
  );
};
